import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import useBlazePose from "../hooks/useBlazePose";
import useAudio from "../hooks/useAudio";
import { gameModes } from "../game/modes/definitions";
import "../styles/Layout.css";
import "../styles/Components.css";
import "../styles/Game.css";

// Import all possible game components
import TestMode from "../game/components/TestMode";
import ComboMode from "../game/components/ComboMode";
import RestMode from "../game/components/RestMode";
import useGameEngine from "../hooks/useGameEngine";

const componentMapping = {
  testMode: TestMode,
  comboMode: ComboMode,
  restMode: RestMode,
};

const GameEngineScreen = ({ mode, onBack, appSize }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const {
    playPunchSound,
    playBellSound,
    playBeepSound,
    playPopSound,
    playDodgeSound,
    playRestSound,
    playBeaten,
    isLoaded: isAudioLoaded,
  } = useAudio();
  const { isReady: isPoseReady, ...poseProps } = useBlazePose({
    videoRef,
    canvasRef,
  });

  const { conditions, poses, position, actions, consumeAction } = poseProps;

  const gameMode = gameModes[mode];
  const [phaseIndex, setPhaseIndex] = useState(0);

  // --- Game Engine Integration ---
  const currentPhase = useMemo(
    () => (gameMode ? gameMode.phases[phaseIndex] : null),
    [gameMode, phaseIndex]
  );
  const isComboMode = currentPhase?.components[0]?.name === "comboMode";
  const comboTimeFactor = currentPhase?.components[0]?.comboTimeFactor;

  const { gameState, gameContext, send } = useGameEngine({
    poses,
    actions,
    consumeAction,
    comboTimeFactor,
    mode,
    currentPhase,
    playPunchSound, // Pass the punch sound function to the game engine
    playPopSound, // Pass the pop sound function for combo generation
    playDodgeSound,
    position,
    playBeaten,
  });

  useEffect(() => {
    if (isComboMode) {
      send("START");
    }
  }, [isComboMode, send]);
  // --- End Game Engine Integration ---

  // Gracefully handle invalid game modes
  useEffect(() => {
    if (!gameMode) {
      onBack();
    }
  }, [gameMode, onBack]);

  const [timeLeft, setTimeLeft] = useState(
    gameMode ? gameMode.phases[phaseIndex]?.duration : 0
  );

  const goToNextPhase = useCallback(() => {
    // If we are in combo mode, the FSM controls the end of the phase
    if (isComboMode) {
      // Logic to transition can be based on gameState === 'complete'
      // For now, we'll let the timer handle it or manually transition
    }

    if (gameMode && phaseIndex < gameMode.phases.length - 1) {
      const nextPhaseIndex = phaseIndex + 1;
      const nextPhase = gameMode.phases[nextPhaseIndex];

      // Play bell sound when transitioning to a new round (not rest phases)
      if (nextPhase.components[0]?.name === "comboMode") {
        playBellSound();
      }

      setPhaseIndex(nextPhaseIndex);
      setTimeLeft(gameMode.phases[nextPhaseIndex].duration);
    } else {
      // Play bell sound when workout is complete
      playBellSound();
      onBack();
    }
  }, [gameMode, phaseIndex, onBack, isComboMode, playBellSound]);

  useEffect(() => {
    //if (!gameMode || isComboMode) return; // Timer is disabled for ComboMode for now

    const timer = setTimeout(() => {
      if (timeLeft > 0) {
        // Play beep sound for last 3 seconds countdown
        if (timeLeft <= 3 && timeLeft > 0) {
          playBeepSound();
        }

        setTimeLeft(timeLeft - 1);
      } else {
        // Play bell sound when round ends
        if (currentPhase?.components[0]?.name === "comboMode") {
          playBellSound();
        }
        goToNextPhase();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    timeLeft,
    phaseIndex,
    gameMode,
    goToNextPhase,
    isComboMode,
    playBeepSound,
    playBellSound,
    currentPhase,
  ]);

  useEffect(() => {
    if (gameMode) {
      const newTimeLeft = gameMode.phases[phaseIndex]?.duration;
      setTimeLeft(newTimeLeft);

      // Play bell sound when starting a new round (not rest phases)
      if (gameMode.phases[phaseIndex]?.components[0]?.name === "comboMode") {
        playBellSound();
      }
    }
  }, [phaseIndex, gameMode, playBellSound]);

  useEffect(() => {
    // When the FSM completes, we can move to the next phase.
    if (isComboMode && gameState === "complete") {
      goToNextPhase();
    }
  }, [isComboMode, gameState, goToNextPhase]);

  if (!gameMode) {
    return null; // Render nothing if the mode is invalid
  }

  const CurrentComponent = componentMapping[currentPhase.components[0].name];

  return (
    <div className="game-screen">
      <header className="game-header">
        <div className="back-button-container">
          <button className="back-button" onClick={onBack}>
            Back
          </button>
        </div>

        <div className="timer-container flex-column">
          <div className="timer-display text-center">{timeLeft}</div>
        </div>

        <div className="phase-info-container">
          <div className="phase-badge">{currentPhase.name}</div>
        </div>
      </header>

      <div className="game-video-area">
        <video
          ref={videoRef}
          className="fullscreen-video"
          autoPlay
          playsInline
        ></video>
        <canvas ref={canvasRef} className="fullscreen-canvas"></canvas>
      </div>

      <div className="game-content-overlay">
        {CurrentComponent && isComboMode && (
          <CurrentComponent
            gameState={gameState}
            gameContext={gameContext}
            send={send}
            appSize={appSize}
          />
        )}
        {CurrentComponent && !isComboMode && (
          <CurrentComponent
            conditions={conditions}
            poses={poses}
            position={position}
            actions={actions}
            consumeAction={consumeAction}
            appSize={appSize}
          />
        )}
      </div>
    </div>
  );
};

export default GameEngineScreen;
